import React from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 900px;
  margin: 80px auto 40px;
  font-family: var(--font-primary);
  
  ${mediaQueries.medium} {
    margin: 60px auto 30px;
  }
  
  ${mediaQueries.small} {
    margin: 40px auto 20px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  margin-bottom: 30px;
  color: ${({theme}) => theme.colors.primary};
  
  ${mediaQueries.small} {
    font-size: 28px;
  }
`;

const Subtitle = styled.h2`
  font-size: 24px;
  margin: 30px 0 20px;
  color: ${({theme}) => theme.colors.secondary};
  
  ${mediaQueries.small} {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const CompanyName = styled.strong`
  font-weight: var(--font-weight-semibold);
`;

const List = styled.ul`
  margin-bottom: 20px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const UpdateDate = styled.p`
  font-size: 14px;
  font-style: italic;
  margin-top: 40px;
  color: ${({theme}) => theme.colors.darkGray};
`;

const PolitiqueConfidentialite: React.FC = () => {
  return (
    <PageContainer>
      <SEO 
        title="Politique de confidentialité" 
        description="Politique de confidentialité de Thinkfab SAS. Découvrez comment nous protégeons vos données personnelles et respectons votre vie privée."
        canonical={`${window.location.origin}/politique-confidentialite`}
      />
      <Header backgroundColor="transparent" textColor="#DD3114" />
      
      <MainSection>
        <Title>Politique de Confidentialité</Title>
        <UpdateDate>Dernière mise à jour : 23/04/2025</UpdateDate>
        
        <Subtitle>Introduction</Subtitle>
        <Paragraph>
          La présente politique de confidentialité définit la manière dont Thinkfab SAS, opérant sous la signature "we do dev", collecte, utilise, conserve et divulgue les informations recueillies auprès des utilisateurs du site thinkfab.fr.
        </Paragraph>
        
        <Subtitle>Collecte des données personnelles</Subtitle>
        <Paragraph>
          Nous pouvons collecter des informations personnelles vous concernant dans les cas suivants :
        </Paragraph>
        <List>
          <ListItem>Lorsque vous remplissez un formulaire de contact</ListItem>
          <ListItem>Lorsque vous vous inscrivez à notre newsletter</ListItem>
          <ListItem>Lorsque vous demandez un devis</ListItem>
          <ListItem>Lorsque vous postulez à une offre d'emploi</ListItem>
        </List>
        <Paragraph>
          Les informations personnelles collectées peuvent inclure votre nom, prénom, adresse email, numéro de téléphone, nom de l'entreprise et poste occupé.
        </Paragraph>
        
        <Subtitle>Utilisation des données collectées</Subtitle>
        <Paragraph>
          Les données que nous collectons sont utilisées aux fins suivantes :
        </Paragraph>
        <List>
          <ListItem>Répondre à vos demandes de renseignements</ListItem>
          <ListItem>Vous fournir les services ou informations demandés</ListItem>
          <ListItem>Améliorer notre service client</ListItem>
          <ListItem>Personnaliser votre expérience utilisateur</ListItem>
          <ListItem>Vous informer sur nos actualités et offres (si vous avez consenti à recevoir ces communications)</ListItem>
        </List>
        
        <Subtitle>Cookies et technologies similaires</Subtitle>
        <Paragraph>
          Notre site peut utiliser des "cookies" pour améliorer l'expérience de l'utilisateur. Les cookies sont de petits fichiers placés sur le disque dur de votre ordinateur. Vous pouvez configurer votre navigateur pour refuser les cookies ou pour être alerté lorsque des cookies sont envoyés.
        </Paragraph>
        <Paragraph>
          Nous utilisons les types de cookies suivants :
        </Paragraph>
        <List>
          <ListItem>Cookies strictement nécessaires au fonctionnement du site</ListItem>
          <ListItem>Cookies analytiques (pour comprendre comment les visiteurs utilisent notre site)</ListItem>
          <ListItem>Cookies fonctionnels (pour mémoriser vos préférences)</ListItem>
        </List>
        
        <Subtitle>Conservation des données</Subtitle>
        <Paragraph>
          Nous conservons vos données personnelles aussi longtemps que nécessaire pour les finalités pour lesquelles nous les avons collectées, y compris pour satisfaire à toute exigence légale ou comptable.
        </Paragraph>
        <List>
          <ListItem>Données de contact : conservées pendant 3 ans à compter du dernier contact</ListItem>
          <ListItem>Données de prospects : conservées pendant 3 ans à compter de leur collecte</ListItem>
          <ListItem>Données clients : conservées pendant la durée de la relation commerciale + 5 ans à des fins de preuve</ListItem>
          <ListItem>Données de candidature : conservées 2 ans après le dernier contact avec le candidat</ListItem>
        </List>
        
        <Subtitle>Partage des données personnelles</Subtitle>
        <Paragraph>
          Nous ne vendons, n'échangeons ni ne transférons vos informations personnelles à des tiers sans votre consentement, sauf si cela est nécessaire pour fournir un service que vous avez demandé ou si la loi nous y oblige.
        </Paragraph>
        <Paragraph>
          Nous pouvons partager des informations avec des prestataires de services tiers qui nous aident à exploiter notre site web ou à gérer notre activité, à condition que ces parties conviennent de garder ces informations confidentielles.
        </Paragraph>
        
        <Subtitle>Vos droits</Subtitle>
        <Paragraph>
          Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants concernant vos données personnelles :
        </Paragraph>
        <List>
          <ListItem>Droit d'accès</ListItem>
          <ListItem>Droit de rectification</ListItem>
          <ListItem>Droit à l'effacement ("droit à l'oubli")</ListItem>
          <ListItem>Droit à la limitation du traitement</ListItem>
          <ListItem>Droit à la portabilité des données</ListItem>
          <ListItem>Droit d'opposition</ListItem>
          <ListItem>Droit de ne pas faire l'objet d'une décision fondée exclusivement sur un traitement automatisé</ListItem>
        </List>
        <Paragraph>
          Pour exercer ces droits, veuillez nous contacter à l'adresse email suivante : [adresse email dédiée RGPD].
        </Paragraph>
        
        <Subtitle>Sécurité des données</Subtitle>
        <Paragraph>
          Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos informations personnelles contre tout accès, utilisation ou divulgation non autorisés.
        </Paragraph>
        
        <Subtitle>Modifications de cette politique</Subtitle>
        <Paragraph>
          Thinkfab SAS se réserve le droit de modifier cette politique de confidentialité à tout moment. Nous vous encourageons à consulter régulièrement cette page pour prendre connaissance des éventuelles modifications.
        </Paragraph>
        
        <Subtitle>Nous contacter</Subtitle>
        <Paragraph>
          Si vous avez des questions concernant cette politique de confidentialité, vous pouvez nous contacter à :
        </Paragraph>
        <Paragraph>
          <CompanyName>Thinkfab SAS</CompanyName><br />
          18 avenue Pierre Mendès France<br />
          67300 Schiltigheim<br />
          Email : info@thinkfab.fr<br />
          Téléphone : +33954570018
        </Paragraph>
      </MainSection>
      
      <Footer />
    </PageContainer>
  );
};

export default PolitiqueConfidentialite;